import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  loading?: boolean;
  children: ReactNode;
};

const VARIANT_CLASSES: Record<Variant, string> = {
  primary: 'bg-[var(--color-btn-primary)] text-white hover:bg-[var(--color-primary-600)] disabled:bg-[var(--color-border)] disabled:text-[var(--color-text-secondary)]',
  secondary: 'bg-white text-[var(--color-text)] border border-[var(--color-border)] hover:border-[var(--color-text-secondary)]',
  ghost: 'bg-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-bg)]',
};

export function Button({ variant = 'primary', loading, children, disabled, className, ...rest }: Props) {
  return (
    <button
      // Default to type="button" so a <Button> inside a <form> doesn't
      // accidentally trigger submit (e.g., a "Resend OTP" button on the
      // OTP form). Callers explicitly pass type="submit" via ...rest when
      // they DO want submission.
      type="button"
      {...rest}
      disabled={disabled || loading}
      className={[
        'w-full min-h-touch px-5 rounded-[var(--radius-lg)] font-semibold text-base',
        'inline-flex items-center justify-center gap-2',
        'transition-colors duration-150',
        VARIANT_CLASSES[variant],
        className || '',
      ].join(' ')}
    >
      {loading ? <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : null}
      {children}
    </button>
  );
}
